import React from 'react';
import { Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * Amber over-budget warning flag for the Work Package Progress table.
 *
 * Shown next to the % Earned column when actual burn significantly exceeds
 * percent earned, signalling that progress may need updating.
 *
 * Display rule:
 *   Show when (actualHrs / budgetHrs × 100) - percentEarned >= 10
 *   (10 percentage POINTS, not 10% of the earned value)
 *
 * Suppressed when:
 *   - budgetHrs is 0 or falsy
 *   - actualHrs is 0 or falsy
 *
 * Props
 * ─────
 * actualHrs       number  Total actual hours from timecards
 * budgetHrs       number  Total budgeted hours
 * percentEarned   number  PM-set percent complete (0–100)
 */
const OverBudgetFlag = ({ actualHrs, budgetHrs, percentEarned }) => {
  if (!budgetHrs || budgetHrs <= 0) return null;
  if (!actualHrs || actualHrs <= 0) return null;

  const actualPct = (actualHrs / budgetHrs) * 100;
  const earned = percentEarned ?? 0;
  const gap = actualPct - earned;

  if (gap < 10) return null;

  const tooltipText =
    `Actual hours burned (${Math.round(actualPct)}%) significantly exceeds ` +
    `% earned (${Math.round(earned)}%) — consider updating progress.`;

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <WarningAmberIcon
        sx={{
          fontSize: 16,
          color: '#BA7517',
          verticalAlign: 'middle',
          ml: 0.5,
          cursor: 'help',
        }}
      />
    </Tooltip>
  );
};

export default OverBudgetFlag;
